import { idToken, isSignedIn, signIn, clearSession, ensureFreshToken } from "./auth";

let apiUrl = "";
export function setApiUrl(u: string) { apiUrl = u; }
export function getApiUrl(): string { return apiUrl; }

export interface Prototype {
  slug: string;
  sk: string;
  title: string;
  description?: string;
  owner: string;
  uploadedAt: string;
  currentVersion: number;
  versionCount: number;
  expiresAt?: number;
  kept?: boolean;
  isInternal?: boolean;
  visibility?: "public" | "internal" | "restricted";
  tags?: string[];
  friendlyName?: string;
  aliasMode?: "latest" | "pinned";
  pinnedVersion?: number;
  hasToken?: boolean;
  previousOwner?: string;
  transferredAt?: string;
  transferredBy?: string;
  // "Update files only" co-editors (Rewind emails). They may push versions and
  // download; they can't change settings, delete, or manage this list.
  collaborators?: string[];
}

export interface Viewer {
  email: string;
  isAdmin: boolean;
}

export interface TransferResponse {
  slug: string;
  owner: string;
  previousOwner: string;
  transferredAt: string;
  tokenRevoked: boolean;
  grantCount: number;
  expiresAt?: number;
}

export interface CreateResponse {
  slug: string;
  uploadUrl: string;
  fields: Record<string, string>;
}

export interface VersionInfo {
  slug: string;
  sk: string;
  version: number;
  uploadedBy: string;
  uploadedAt: string;
  note?: string;
  status: "pending" | "ready" | "failed" | "deleted";
  error?: string;
  fileCount?: number;
  sizeBytes?: number;
  authMethod: "oidc" | "token";
  filesRetained: boolean;
}

export interface TokenResponse {
  token: string;
  expiresAt: number;
}

export interface Grant {
  id: string;
  label: string;
  createdBy: string;
  createdAt: string;
  // Empty means no additional proof is required — the pre-feature behaviour.
  // The API always sends an array, so a missing value here is a stale response
  // rather than "ungated".
  allowedDomains?: string[];
}
export interface CreatedGrant extends Grant { url: string; }

export interface CreateVersionResponse {
  version: number;
  uploadUrl: string;
  fields: Record<string, string>;
}

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  // Try refresh first if we have a session — covers the pre-expiry buffer window
  // where isSignedIn() returns false but the refresh token is still valid.
  try {
    await ensureFreshToken();
  } catch {
    // ensureFreshToken throws if there's no session at all — handled below
  }

  if (!isSignedIn()) {
    clearSession();
    await signIn("none");
    throw new Error("Session expired — signing you back in…");
  }

  const res = await fetch(`${apiUrl}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${idToken()}`,
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) {
    clearSession();
    await signIn("none");
    throw new Error("Session expired — signing you back in…");
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => ({}));
  if (res.status === 403 && !data.error) {
    clearSession();
    await signIn("none");
    throw new Error("Session expired — signing you back in…");
  }
  if (!res.ok) {
    // Callers that need to react to a specific structured 409 (grant create's
    // "prototype expires", a visibility change's "grants exist") read these
    // extra properties — .message alone loses expiresAt/count.
    const err = new Error(data.error ?? `HTTP ${res.status}`) as ApiError;
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return data as T;
}

export interface ApiError extends Error {
  status?: number;
  body?: { error?: string; [key: string]: unknown };
}

export const api = {
  list: () => req<{ prototypes: Prototype[]; viewer?: Viewer }>("GET", "/prototypes"),
  get: (slug: string) => req<Prototype>("GET", `/prototypes/${slug}`),
  create: (title: string, description?: string, kind?: "zip" | "html", visibility?: "internal" | "restricted" | "public", tags?: string[]) =>
    req<CreateResponse>("POST", "/prototypes", {
      title,
      ...(description ? { description } : {}),
      ...(kind === "html" ? { kind } : {}),
      ...(visibility ? { visibility } : {}),
      ...(tags && tags.length ? { tags } : {}),
    }),
  del: (slug: string) => req<void>("DELETE", `/prototypes/${slug}`),
  patch: (slug: string, body: { isInternal?: true; visibility?: "internal" | "restricted"; tags?: string[]; title?: string; description?: string }) =>
    req<{ slug: string; isInternal?: true; visibility?: "internal" | "restricted"; tags?: string[]; title?: string; description?: string }>("PATCH", `/prototypes/${slug}`, body),
  keep: (slug: string) => req<{ kept: boolean }>("PUT", `/prototypes/${slug}/keep`),
  unkeep: (slug: string) => req<{ kept: boolean; expiresAt: number }>("PUT", `/prototypes/${slug}/unkeep`),
  setAlias: (slug: string, body: { name: string; mode: "latest" | "pinned"; version?: number }) =>
    req<{ slug: string; name: string; mode: "latest" | "pinned"; version: number }>("PUT", `/prototypes/${slug}/alias`, body),
  clearAlias: (slug: string) =>
    req<{ slug: string; cleared: true }>("DELETE", `/prototypes/${slug}/alias`),
  verifyAlias: (slug: string) =>
    req<{ ready: boolean; status?: number; reason?: string }>("GET", `/prototypes/${slug}/alias/verify`),
  listVersions: (slug: string) => req<{ versions: VersionInfo[] }>("GET", `/prototypes/${slug}/versions`),
  getVersion: (slug: string, version: number) => req<VersionInfo>("GET", `/prototypes/${slug}/versions/${version}`),
  generateToken: (slug: string) => req<TokenResponse>("POST", `/prototypes/${slug}/token`),
  createVersion: (slug: string, note?: string, uploadedBy?: string, kind?: "zip" | "html", baseVersion?: number) =>
    req<CreateVersionResponse>("POST", `/prototypes/${slug}/versions`, {
      ...(note ? { note } : {}),
      ...(uploadedBy ? { uploadedBy } : {}),
      ...(kind === "html" ? { kind } : {}),
      ...(baseVersion !== undefined ? { baseVersion } : {}),
    }),
  deleteVersion: (slug: string, version: number) =>
    req<void>("DELETE", `/prototypes/${slug}/versions/${version}`),
  transferPrototype: (slug: string, newOwner: string) =>
    req<TransferResponse>("POST", `/prototypes/${slug}/transfer`, { newOwner }),
  listCollaborators: (slug: string) =>
    req<{ collaborators: string[] }>("GET", `/prototypes/${slug}/collaborators`),
  addCollaborator: (slug: string, email: string) =>
    req<{ collaborators: string[] }>("POST", `/prototypes/${slug}/collaborators`, { email }),
  removeCollaborator: (slug: string, email: string) =>
    req<{ collaborators: string[] }>("DELETE", `/prototypes/${slug}/collaborators/${encodeURIComponent(email)}`),
  getDownload: (slug: string) =>
    req<{ version: number; files: { path: string; url: string }[]; expiresAt: string }>("GET", `/prototypes/${slug}/download`),
};

export async function listGrants(slug: string): Promise<Grant[]> {
  const { grants } = await req<{ grants: Grant[] }>("GET", `/prototypes/${slug}/grants`);
  return grants;
}

export async function createGrant(
  slug: string,
  label: string,
  allowedDomains?: string[],
): Promise<CreatedGrant> {
  // Omitted entirely when empty, so an ungated create is byte-identical to what
  // this sent before the feature existed.
  return req<CreatedGrant>("POST", `/prototypes/${slug}/grants`, {
    label,
    ...(allowedDomains?.length ? { allowedDomains } : {}),
  });
}

export async function setGrantDomains(
  slug: string,
  id: string,
  allowedDomains: string[],
): Promise<Grant> {
  return req<Grant>("PATCH", `/prototypes/${slug}/grants/${id}`, { allowedDomains });
}

export async function revokeGrant(slug: string, id: string): Promise<void> {
  await req<void>("DELETE", `/prototypes/${slug}/grants/${id}`);
}

export async function uploadFile(
  file: File,
  presigned: { uploadUrl: string; fields: Record<string, string> },
): Promise<void> {
  const form = new FormData();
  // presigned.fields already includes Content-Type and policy — append all fields first
  for (const [k, v] of Object.entries(presigned.fields)) form.append(k, v);
  // file MUST be last
  form.append("file", file);
  const res = await fetch(presigned.uploadUrl, { method: "POST", body: form });
  if (!res.ok) throw new Error(`S3 upload failed: ${res.status}`);
}
